// components/ProcessFlowDiagram.js
import React from 'react';

const ProcessFlowDiagram = ({ activeUnits, streams, onStreamSelect, selectedStream }) => {
  // SVG dimensions
  const width = 1100;
  const height = 700;
  
  // Define unit positions in the diagram
  const unitPositions = {
    feed: { x: 50, y: 200, width: 120, height: 60 },
    fieldCompression: { x: 220, y: 200, width: 140, height: 60 },
    inletSeparation: { x: 430, y: 200, width: 140, height: 60 },
    inletCompression: { x: 430, y: 100, width: 140, height: 60 },
    stabilisation: { x: 430, y: 300, width: 140, height: 60 },
    wasteWaterDisposal: { x: 430, y: 400, width: 140, height: 60 },
    sweetening: { x: 630, y: 100, width: 140, height: 60 },
    dehydration: { x: 830, y: 100, width: 140, height: 60 },
    dewpoint: { x: 830, y: 200, width: 140, height: 60 },
    fractionation: { x: 830, y: 300, width: 140, height: 60 },
    salesCompression: { x: 1000, y: 100, width: 80, height: 60 },
    utilities: { x: 830, y: 500, width: 140, height: 60 },
  };
  
  // Define stream paths
  // For each stream, define the start and end unit, and intermediate points if needed
  const streamPaths = {
    feed: {
      start: 'feed',
      end: 'fieldCompression',
      points: [],
    },
    fieldCompressionOut: {
      start: 'fieldCompression',
      end: 'inletSeparation',
      points: [],
    },
    inletSeparationVaporOut: {
      start: 'inletSeparation',
      end: 'inletCompression',
      points: [
        { x: unitPositions.inletSeparation.x + unitPositions.inletSeparation.width / 2, y: unitPositions.inletCompression.y + unitPositions.inletCompression.height / 2 }
      ],
    },
    inletSeparationLiquidOut: {
      start: 'inletSeparation',
      end: 'stabilisation',
      points: [
        { x: unitPositions.inletSeparation.x + unitPositions.inletSeparation.width / 2, y: unitPositions.stabilisation.y + unitPositions.stabilisation.height / 2 }
      ],
    },
    inletCompressionOut: {
      start: 'inletCompression',
      end: 'sweetening',
      points: [],
    },
    stabilizationGasOut: {
      start: 'stabilisation',
      end: 'inletSeparation',
      points: [
        { x: unitPositions.stabilisation.x - 30, y: unitPositions.stabilisation.y + unitPositions.stabilisation.height / 2 },
        { x: unitPositions.stabilisation.x - 30, y: unitPositions.inletSeparation.y + unitPositions.inletSeparation.height + 20 },
        { x: unitPositions.inletSeparation.x + unitPositions.inletSeparation.width / 2, y: unitPositions.inletSeparation.y + unitPositions.inletSeparation.height + 20 },
        { x: unitPositions.inletSeparation.x + unitPositions.inletSeparation.width / 2, y: unitPositions.inletSeparation.y + unitPositions.inletSeparation.height },
      ],
    },
    stabilizationLiquidOut: {
      start: 'stabilisation',
      end: 'fractionation',
      points: [
        { x: unitPositions.stabilisation.x + unitPositions.stabilisation.width, y: unitPositions.stabilisation.y + unitPositions.stabilisation.height / 2 },
        { x: unitPositions.fractionation.x - 50, y: unitPositions.stabilisation.y + unitPositions.stabilisation.height / 2 },
        { x: unitPositions.fractionation.x - 50, y: unitPositions.fractionation.y + unitPositions.fractionation.height / 2 },
      ],
    },
    sweetGasOut: {
      start: 'sweetening',
      end: 'dehydration',
      points: [],
    },
    acidGasOut: {
      start: 'sweetening',
      end: 'utilities',
      points: [
        { x: unitPositions.sweetening.x + unitPositions.sweetening.width / 2, y: unitPositions.utilities.y - 50 },
        { x: unitPositions.utilities.x - 30, y: unitPositions.utilities.y - 50 },
        { x: unitPositions.utilities.x - 30, y: unitPositions.utilities.y + unitPositions.utilities.height / 2 },
      ],
    },
    dehydrationOut: {
      start: 'dehydration',
      end: 'dewpoint',
      points: [
        { x: unitPositions.dehydration.x + unitPositions.dehydration.width / 2, y: unitPositions.dewpoint.y - 30 },
        { x: unitPositions.dewpoint.x + unitPositions.dewpoint.width / 2, y: unitPositions.dewpoint.y - 30 },
        { x: unitPositions.dewpoint.x + unitPositions.dewpoint.width / 2, y: unitPositions.dewpoint.y },
      ],
    },
    dewpointResidueGasOut: {
      start: 'dewpoint',
      end: 'salesCompression',
      points: [
        { x: unitPositions.dewpoint.x + unitPositions.dewpoint.width, y: unitPositions.dewpoint.y + unitPositions.dewpoint.height / 3 },
        { x: unitPositions.salesCompression.x - 20, y: unitPositions.dewpoint.y + unitPositions.dewpoint.height / 3 },
        { x: unitPositions.salesCompression.x - 20, y: unitPositions.salesCompression.y + unitPositions.salesCompression.height / 2 },
      ],
    },
    dewpointNglOut: {
      start: 'dewpoint',
      end: 'fractionation',
      points: [
        { x: unitPositions.dewpoint.x + unitPositions.dewpoint.width / 2, y: unitPositions.fractionation.y - 30 },
        { x: unitPositions.fractionation.x + unitPositions.fractionation.width / 2, y: unitPositions.fractionation.y - 30 },
        { x: unitPositions.fractionation.x + unitPositions.fractionation.width / 2, y: unitPositions.fractionation.y },
      ],
    },
    salesGasOut: {
      start: 'salesCompression',
      end: null,
      points: [
        { x: unitPositions.salesCompression.x + unitPositions.salesCompression.width + 30, y: unitPositions.salesCompression.y + unitPositions.salesCompression.height / 2 },
      ],
    },
  };
  
  // Helper function to generate SVG path from stream definition
  const generatePath = (streamDef) => {
    if (!streamDef) return '';
    
    const startUnit = unitPositions[streamDef.start];
    const endUnit = streamDef.end ? unitPositions[streamDef.end] : null;
    
    if (!startUnit) return '';
    
    let path = `M ${startUnit.x + startUnit.width} ${startUnit.y + startUnit.height / 2}`;
    
    // Add intermediate points if defined
    if (streamDef.points && streamDef.points.length > 0) {
      streamDef.points.forEach(point => {
        path += ` L ${point.x} ${point.y}`;
      });
    } else if (endUnit) {
      // Direct line if no intermediate points
      path += ` L ${endUnit.x} ${endUnit.y + endUnit.height / 2}`;
    }
    
    return path;
  };
  
  // Filter stream paths based on active units
  const activeStreamPaths = {};
  Object.entries(streamPaths).forEach(([streamName, pathDef]) => {
    // Check if both start and end units are active or special cases
    const startActive = pathDef.start === 'feed' || activeUnits[pathDef.start];
    const endActive = !pathDef.end || pathDef.end === 'feed' || activeUnits[pathDef.end];
    
    // Special case for utilities
    if (pathDef.end === 'utilities' && activeUnits.utilities === 'none') return;
    
    // Special cases for inter-unit connections
    if (streamName === 'inletSeparationVaporOut' && !activeUnits.inletCompression) return;
    if (streamName === 'stabilizationGasOut' && (!activeUnits.stabilisation || !activeUnits.inletSeparation)) return;
    if (streamName === 'inletCompressionOut' && !activeUnits.sweetening) return;
    if (streamName === 'sweetGasOut' && !activeUnits.dehydration) return;
    if (streamName === 'dehydrationOut' && !activeUnits.dewpoint) return;
    if (streamName === 'dewpointResidueGasOut' && !activeUnits.salesCompression) return;
    if (streamName === 'dewpointNglOut' && !activeUnits.fractionation) return;
    
    if (startActive && endActive) {
      activeStreamPaths[streamName] = pathDef;
    }
  });
  
  // Render active units
  const renderUnits = () => {
    return (
      <>
        {/* Feed is always shown */}
        <g 
          className="unit-box feed"
          onClick={() => onStreamSelect('feed')}
        >
          <rect
            x={unitPositions.feed.x}
            y={unitPositions.feed.y}
            width={unitPositions.feed.width}
            height={unitPositions.feed.height}
            rx="10"
            className={selectedStream === 'feed' ? 'selected' : ''}
          />
          <text
            x={unitPositions.feed.x + unitPositions.feed.width / 2}
            y={unitPositions.feed.y + unitPositions.feed.height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            Feed
          </text>
        </g>
        
        {/* Render active units */}
        {Object.entries(activeUnits).map(([unitName, isActive]) => {
          if (!isActive || isActive === 'none' || !unitPositions[unitName]) return null;
          
          const unit = unitPositions[unitName];
          return (
            <g 
              key={unitName}
              className={`unit-box ${unitName}`}
              onClick={() => onStreamSelect(unitName)}
            >
              <rect
                x={unit.x}
                y={unit.y}
                width={unit.width}
                height={unit.height}
                rx="10"
                className={selectedStream === unitName ? 'selected' : ''}
              />
              <text
                x={unit.x + unit.width / 2}
                y={unit.y + unit.height / 2}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {unitName === 'utilities' 
                  ? `Utilities (${activeUnits.utilities})`
                  : unitName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </text>
            </g>
          );
        })}
        
        {/* Add sales gas output indicator */}
        <g className="output-indicator">
          <circle 
            cx={unitPositions.salesCompression.x + unitPositions.salesCompression.width + 30}
            cy={unitPositions.salesCompression.y + unitPositions.salesCompression.height / 2}
            r="15"
            className={selectedStream === 'salesGasOut' ? 'selected' : ''}
            onClick={() => onStreamSelect('salesGasOut')}
          />
          <text
            x={unitPositions.salesCompression.x + unitPositions.salesCompression.width + 30}
            y={unitPositions.salesCompression.y + unitPositions.salesCompression.height / 2 - 25}
            textAnchor="middle"
          >
            Sales Gas
          </text>
        </g>
        
        {/* Add NGL product indicators */}
        {activeUnits.fractionation && (
          <g className="output-indicator">
            <circle 
              cx={unitPositions.fractionation.x + unitPositions.fractionation.width + 30}
              cy={unitPositions.fractionation.y + unitPositions.fractionation.height / 2}
              r="15"
              className={selectedStream === 'nglProducts' ? 'selected' : ''}
              onClick={() => onStreamSelect('fractionationProducts')}
            />
            <text
              x={unitPositions.fractionation.x + unitPositions.fractionation.width + 30}
              y={unitPositions.fractionation.y + unitPositions.fractionation.height / 2 - 25}
              textAnchor="middle"
            >
              NGL Products
            </text>
          </g>
        )}
      </>
    );
  };
  
  // Render streams
  const renderStreams = () => {
    return Object.entries(activeStreamPaths).map(([streamName, pathDef]) => {
      // Skip if stream data doesn't exist
      if (!streams || !streams[streamName]) return null;
      
      const streamData = streams[streamName] || {};
      
      // Format tooltip data with null checks
        const streamFlowRate = streamData.flowRate ? `${streamData.flowRate.toFixed(1)} kmol/h` : 'N/A';
        const streamTemp = streamData.temperature ? `${streamData.temperature.toFixed(1)} °C` : 'N/A';
        const streamPressure = streamData.pressure ? `${streamData.pressure.toFixed(1)} bar` : 'N/A';
            
      return (
        <g key={streamName} className="stream-path">
          <path
            d={generatePath(pathDef)}
            className={selectedStream === streamName ? 'selected' : ''}
            onClick={() => onStreamSelect(streamName)}
          />
          
          {/* Add flow direction arrow */}
          {pathDef.points && pathDef.points.length > 0 && (
            <circle
              cx={pathDef.points[0].x}
              cy={pathDef.points[0].y}
              r="3"
              onClick={() => onStreamSelect(streamName)}
            />
          )}
          
          {/* Add stream label */}
          <g className="stream-label">
            <title>{`${streamName}: ${streamFlowRate}, ${streamTemp}, ${streamPressure}`}</title>
          </g>
        </g>
      );
    });
  };
  
  return (
    <div className="process-flow-diagram">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Render streams first so units appear on top */}
        {renderStreams()}
        {renderUnits()}
      </svg>
      
      {selectedStream && streams && streams[selectedStream] && (
        // In the selected stream info section, change:
        <div className="selected-stream-info">
        <h3>Selected Stream: {selectedStream}</h3>
        <table>
            <tbody>
            <tr>
                <td>Flow Rate:</td>
                <td>{streams[selectedStream]?.flowRate?.toFixed(1) || 'N/A'} kmol/h</td>
            </tr>
            <tr>
                <td>Temperature:</td>
                <td>{streams[selectedStream]?.temperature?.toFixed(1) || 'N/A'} °C</td>
            </tr>
            <tr>
                <td>Pressure:</td>
                <td>{streams[selectedStream]?.pressure?.toFixed(1) || 'N/A'} bar</td>
            </tr>
            </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default ProcessFlowDiagram;